import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Building2, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CrossTenantConversationList } from '@up2d8/contracts';
import { Avatar, Badge, Card } from '@up2d8/ui';
import { useSession } from '../../../shared/session';
import { uiLocale } from '../../../shared/format';

/**
 * De gesprekken over bedrijven heen, boven de gewone gesprekken (ACC-4, ADR-0082).
 *
 * Bestaat alleen als de server zegt dat het mag: zonder het recht geeft de lijst `404`, en dan
 * staat hier niets, ook geen lege kaart. Elke rij draagt de badge "Over bedrijven heen" en de
 * bedrijfsnaam van de ander, zodat niemand denkt dat een collega uit het eigen bedrijf meeleest.
 */
export function useCrossTenantConversations() {
  const { api, profile } = useSession();
  return useQuery({
    queryKey: ['shared-conversations', profile?.tenantId],
    enabled: Boolean(profile?.tenantId),
    refetchInterval: 30_000,
    retry: false,
    queryFn: async (): Promise<CrossTenantConversationList | null> => {
      const { data, response } = await api.GET('/api/v1/shared-conversations');
      if (response.status === 404) return null;
      if (!data) throw new Error('Ophalen mislukt');
      return data as CrossTenantConversationList;
    },
  });
}

export function CrossTenantSection() {
  const { t } = useTranslation();
  const conversations = useCrossTenantConversations();
  const list = conversations.data;
  if (!list) return null;

  return (
    <Card className="mb-element" data-testid="cross-tenant-section">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-heading text-content font-semibold text-text-primary">
          <Building2 aria-hidden className="h-4 w-4 text-brand-primary" />
          {t('crossTenant.title')}
        </h2>
        <Link
          to="/app/over-bedrijven/nieuw"
          data-touch-target=""
          data-testid="cross-tenant-new"
          className="inline-flex min-h-touch items-center gap-1 rounded-pill px-2 text-small text-brand-primary hover:bg-surface-sunk"
        >
          <Plus aria-hidden className="h-4 w-4" />
          {t('crossTenant.new')}
        </Link>
      </div>

      {list.items.length === 0 ? (
        <p className="text-small text-text-muted">{t('crossTenant.empty')}</p>
      ) : (
        <ul className="flex flex-col">
          {list.items.map((conversation) => (
            <li key={conversation.id}>
              <Link
                to="/app/over-bedrijven/$conversationId"
                params={{ conversationId: conversation.id }}
                data-touch-target=""
                data-testid="cross-tenant-conversation"
                className="flex min-h-touch items-center gap-element border-b border-border-light py-2 last:border-b-0 hover:bg-surface-sunk"
              >
                <Avatar name={conversation.title} size="md" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate font-heading text-content font-medium text-text-primary">
                      {conversation.title}
                    </span>
                    {conversation.lastMessageAt && (
                      <span className="shrink-0 text-caption text-text-muted">
                        {new Date(conversation.lastMessageAt).toLocaleDateString(uiLocale(), {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    <Badge tone="cyan">{t('crossTenant.badge')}</Badge>
                    <span className="truncate text-caption text-text-secondary">
                      {conversation.otherTenantNames.join(', ')}
                    </span>
                  </div>
                  {conversation.lastMessagePreview && (
                    <p className="truncate text-small text-text-muted">
                      {conversation.lastMessageMine ? `${t('crossTenant.you')}: ` : ''}
                      {conversation.lastMessagePreview}
                    </p>
                  )}
                </div>
                {conversation.unreadCount > 0 && (
                  <span className="shrink-0 rounded-pill bg-brand-orange px-2 text-caption font-semibold text-text-on-dark">
                    {conversation.unreadCount}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
